(() => {
  const SCHEMA_VERSION = "ago.clearance.lifecycle.v1";
  const CONTRACT_REVISION = 2;
  const LAST_DAY_MS = 24 * 60 * 60 * 1000;
  const roots = new Set();
  let timer = null;

  const parsePayload = (root) => {
    const node = root.nextElementSibling;
    if (!node || !node.matches("script[data-ago-clearance-variants]")) return new Map();
    try {
      const values = JSON.parse(node.textContent || "[]");
      return new Map(values.map((entry) => [String(entry.id), entry]));
    } catch {
      return new Map();
    }
  };

  const dateLabel = (projection) => {
    const label = projection.dateMarkType === "BEST_BEFORE"
      ? "Best before"
      : projection.dateMarkType === "USE_BY"
        ? "Use by"
        : "Product date";
    const date = new Date(projection.customerExpiryAt);
    if (Number.isNaN(date.getTime())) return label;
    return `${label} ${new Intl.DateTimeFormat("en-AU", {
      timeZone: "Australia/Sydney",
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(date)}`;
  };

  const validEntry = (root, entry, variantId, now) => {
    const projection = entry?.projection;
    if (root.dataset.productClearance !== "true" || entry?.available !== true || !projection) return false;
    if (projection.schemaVersion !== SCHEMA_VERSION || projection.contractRevision !== CONTRACT_REVISION || projection.state !== "ACTIVE") return false;
    if (projection.variantId !== `gid://shopify/ProductVariant/${variantId}`) return false;
    const customerExpiryAt = Date.parse(projection.customerExpiryAt || "");
    const autoExitAt = Date.parse(projection.autoExitAt || "");
    return Number.isFinite(customerExpiryAt) && Number.isFinite(autoExitAt) && customerExpiryAt > now && autoExitAt > now;
  };

  const mountDailyComboCardStatus = (card) => {
    if (!card?.hasAttribute("data-ago-clearance-daily-combo")) return;
    const productCard = card.closest(".pr_grid_item, .prs_slide_item");
    const imageSlot = productCard?.querySelector(".product-image, .prs_slide_wrap");
    const date = card.querySelector(".ago-clearance-card__date")?.textContent?.trim();
    if (!imageSlot || !date) return;

    let status = imageSlot.querySelector("[data-ago-clearance-daily-status]");
    if (!status) {
      status = document.createElement("span");
      status.className = "ago-clearance-card__daily-status";
      status.setAttribute("data-ago-clearance-daily-status", "");
      imageSlot.appendChild(status);
    }
    status.textContent = date;
    status.setAttribute("aria-label", `${card.querySelector(".ago-clearance-card__badge")?.textContent?.trim() || "Clearance Special"}: ${date}`);
  };

  const mountDailyComboCardStatuses = (scope = document) => {
    scope.querySelectorAll("[data-ago-clearance-daily-combo]").forEach(mountDailyComboCardStatus);
  };

  const selectedVariantId = (root) => {
    const fromUrl = new URL(window.location.href).searchParams.get("variant");
    return fromUrl || root.dataset.selectedVariantId || "";
  };

  const renderRoot = (root, variantId = selectedVariantId(root)) => {
    const values = root._agoClearanceValues || (root._agoClearanceValues = parsePayload(root));
    const entry = values.get(String(variantId));
    const now = Date.now();
    if (!validEntry(root, entry, variantId, now)) {
      root.classList.add("ago-clearance-pdp--hidden");
      return;
    }

    const projection = entry.projection;
    root.classList.remove("ago-clearance-pdp--hidden");
    root.querySelector("[data-ago-clearance-date]").textContent = dateLabel(projection);
    const countdown = root.querySelector("[data-ago-clearance-countdown]");
    const remaining = Date.parse(projection.autoExitAt) - now;
    if (remaining <= LAST_DAY_MS) {
      const totalMinutes = Math.max(0, Math.ceil(remaining / 60_000));
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      countdown.textContent = `${root.dataset.countdownPrefix || "Clearance Special ends in"} ${hours}h ${String(minutes).padStart(2, "0")}m`;
      countdown.hidden = false;
    } else {
      countdown.hidden = true;
      countdown.textContent = "";
    }
  };

  const fillFeed = async (feed) => {
    if (feed.dataset.loading === "true") return;
    feed.dataset.loading = "true";
    const targetCount = Number.parseInt(feed.dataset.targetCount || "0", 10);
    let nextUrl = feed.dataset.nextUrl || "";

    try {
      while (nextUrl && feed.querySelectorAll(".pr_grid_item").length < targetCount) {
        const response = await fetch(nextUrl, { credentials: "same-origin" });
        if (!response.ok) break;
        const documentCopy = new DOMParser().parseFromString(await response.text(), "text/html");
        const nextFeed = documentCopy.querySelector("[data-ago-clearance-feed]");
        if (!nextFeed) break;
        nextFeed.querySelectorAll(".pr_grid_item").forEach((card) => feed.appendChild(card));
        nextUrl = nextFeed.dataset.nextUrl || "";
        feed.dataset.nextUrl = nextUrl;
      }
      mountDailyComboCardStatuses(feed);
      feed.dispatchEvent(new CustomEvent("ago:clearance-feed-ready", { bubbles: true }));
    } finally {
      feed.dataset.loading = "false";
    }
  };

  const renderAll = () => {
    if (document.visibilityState !== "visible") return;
    roots.forEach((root) => renderRoot(root));
  };

  const startTimer = () => {
    window.clearInterval(timer);
    timer = roots.size && document.visibilityState === "visible"
      ? window.setInterval(renderAll, 60_000)
      : null;
  };

  const boot = (scope = document) => {
    scope.querySelectorAll("[data-ago-clearance-pdp]").forEach((root) => {
      roots.add(root);
      renderRoot(root);
    });
    scope.querySelectorAll("[data-ago-clearance-feed]").forEach(fillFeed);
    mountDailyComboCardStatuses(scope);
    startTimer();
  };

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") renderAll();
    startTimer();
  });
  document.addEventListener("DOMContentLoaded", () => boot());
  document.addEventListener("shopify:section:load", (event) => boot(event.target));
  if (window.jQuery_T4NT) {
    window.jQuery_T4NT(document).on("variant:change", (_event, variant) => {
      if (!variant?.id) return;
      roots.forEach((root) => renderRoot(root, variant.id));
    });
  }
})();
